import React, {useMemo, useRef} from "react";
import yetiz from "yetiz";
import Card from "@mui/material/Card";
import {CardContent} from "@mui/material";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Typography from "@mui/material/Typography";

export const useYetizSnowProfileProperties = () => {
    const [showRamm, setShowRamm] = React.useState(true);
    const [showStratigraphy, setShowStratigraphy] = React.useState(true);
    const [showCrystals, setShowCrystals] = React.useState(true);
    const [showLayerComments, setShowLayerComments] = React.useState(false);
    const [showFlags, setShowFlags] = React.useState(true);

    const toggleRamm = () => setShowRamm((prev) => !prev);
    const toggleStratigraphy = () => setShowStratigraphy((prev) => !prev);
    const toggleCrystals = () => setShowCrystals((prev) => !prev);
    const toggleLayerComments = () => setShowLayerComments((prev) => !prev);
    const toggleFlags = () => setShowFlags((prev) => !prev);

    const yetizProperties = useMemo(() => ({
        show_ramm: showRamm,
        hide_hardness_profile: !showStratigraphy,
        monochrome_hardness_profile: !showCrystals,
        show_layer_comments: showLayerComments,
        show_flags: showFlags,
    }), [showRamm, showStratigraphy, showCrystals, showLayerComments, showFlags])

    return {
        showRamm,
        toggleRamm,
        showStratigraphy,
        toggleStratigraphy,
        showCrystals,
        toggleCrystals,
        showLayerComments,
        toggleLayerComments,
        showFlags,
        toggleFlags,
        yetizProperties
    }
}

export const useYetizSnowProfileMenu = ({model23}) => {
    const graphRef = useRef(null);

    const currentProfileName = model23 ? `${model23.station.code}_${model23.datetime}` : 'profile';

    const handleDownloadPDF = () => {
        yetiz.Graph.convert("PDF", graphRef.current, (buffer) => {
            downloadFile(buffer, `${currentProfileName}.pdf`, "PDF");
        });
    }

    const handleDownloadPNG = () => {
        yetiz.Graph.convert("PNG", graphRef.current, (buffer) => {
            downloadFile(buffer, `${currentProfileName}.png`, "PNG");
        });
    }

    return {
        graphRef,
        handleDownloadPDF,
        handleDownloadPNG,
    }
}

export const useYetizSnowProfileZoom = ({initialZoom, minZoom=0.5, maxZoom=1.5, width=1000, height=1414}) => {
    const [zoom, setZoom] = React.useState(initialZoom);

    const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.1, maxZoom));
    const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.1, minZoom));

    return {
        zoom,
        setZoom,
        handleZoomIn,
        handleZoomOut,
        zoomInEnabled: zoom < maxZoom,
        zoomOutEnabled: zoom > minZoom,
        width: Math.round(width * zoom),
        height: Math.round(height * zoom),
    }
}

async function downloadFile(buffer, filename, type) {
    let blob;
    if(type==="PDF") {
        blob = new Blob([buffer], { type: "application/pdf" });
    } else if (type==="PNG") {
        blob = new Blob([buffer], { type: "image/png" });
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

export const YetizSnowProfileContainer = ({width, height, children, sx={}}) => {
    return <Card sx={{width: width + 48, height: height + 48, overflow: "hidden", mx: 'auto', ...sx}}>
        <CardContent>
            {children}
        </CardContent>
    </Card>;
}

export const YetizSnowProfileZoomWrapper = ({children, zoom}) => {
    return <div style={{
        transform: `scale(${zoom})`,
        transformOrigin: 'top left'}}>
            {children}
    </div>
}

export const YetizSnowProfileTools = ({onDownloadPDF, onDownloadPNG}) => {
    return <Stack direction="row" justifyContent="end">
        {
            !!onDownloadPDF && <IconButton onClick={onDownloadPDF}>
                <Icon>picture_as_pdf</Icon>
            </IconButton>
        }
        {
            !!onDownloadPNG && <IconButton onClick={onDownloadPNG}>
                <Icon>download</Icon>
            </IconButton>
        }
    </Stack>
}

export const YetizSnowProfileZoom = ({onZoomIn, onZoomOut, zoom, zoomInEnabled, zoomOutEnabled}) => {
    return <Stack direction="row" justifySelf="start" alignItems="center">
        <IconButton onClick={onZoomOut} disabled={!zoomOutEnabled}>
            <Icon>zoom_out</Icon>
        </IconButton>
        <Typography variant="body2">
            {`${Math.round(zoom * 100)} %`}
        </Typography>
        <IconButton onClick={onZoomIn} disabled={!zoomInEnabled}>
            <Icon>zoom_in</Icon>
        </IconButton>
    </Stack>
}
